import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTime, fmtBytes } from "../../lib/api";
import { Card, Alert } from "../ui-bits";
import { ClipCard, toClip } from "../broll/clip-utils";
import { useQuota } from "../../context/QuotaContext";
import { toast } from "sonner";
import { FolderOpen, Folder, Film, ArrowLeft, Trash2, Loader2 } from "lucide-react";

// Sección propia "Mi Carpeta" (ago-2026): a diferencia del MyFolder.jsx embebido dentro de un
// vídeo concreto (pensado para elegir material y añadirlo a una escena), esta es una vista de
// GESTIÓN pura de toda la biblioteca del usuario, entre todos sus vídeos — sin "vídeo actual",
// sin subir, sin "usar en escena". Su único propósito es dejar ver y borrar para liberar
// espacio sin tener que entrar vídeo por vídeo. Reutiliza los mismos endpoints de borrado que
// MyFolder (broll_delete_item / broll_delete_all) y el mismo ClipCard en modo displayOnly.
export default function TabLibrary() {
  const { t } = useTranslation();
  const { quota } = useQuota() || {};
  const [folders, setFolders] = useState(null); // null = cargando
  const [openProjectId, setOpenProjectId] = useState(null);
  const [openFolderId, setOpenFolderId] = useState(null);

  const load = () => {
    api.get("/library").then(({ data }) => setFolders(data.folders || []))
      .catch((e) => { toast.error(apiErr(e)); setFolders([]); });
  };
  useEffect(() => { load(); }, []);

  const openProject = folders?.find((f) => f.project_id === openProjectId) || null;
  const openFolder = openProject?.folders?.find((f) => f.id === openFolderId) || null;

  const goRoot = () => { setOpenProjectId(null); setOpenFolderId(null); };
  const goProject = (pid) => { setOpenProjectId(pid); setOpenFolderId(null); };

  const deleteItem = async (pid, id, label) => {
    if (!window.confirm(t("mf.removeConfirm", { label }))) return;
    try {
      await api.delete(`/projects/${pid}/broll/library/${id}`);
      toast.success(t("mf.removed"));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Usa pf.project_id (la carpeta que se está borrando), no un "proyecto actual" — esta página
  // no tiene uno, cualquiera de las carpetas listadas puede borrarse igual.
  const deleteWholeFolder = async (pf) => {
    if (!pf.count) return;
    if (!window.confirm(t("mf.removeFolderConfirm", { label: pf.name, count: pf.count }))) return;
    try {
      await api.delete(`/projects/${pf.project_id}/broll/library`);
      toast.success(t("mf.folderRemoved"));
      if (pf.project_id === openProjectId) goRoot();
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const storageUsed = quota?.storage_used_bytes || 0;
  const storageLimit = quota?.storage_limit_bytes || 1;
  const storagePct = Math.min(100, Math.round((storageUsed / storageLimit) * 100));

  return (
    <div className="space-y-6" data-testid="library-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{t("library.pageTitle")}</h1>
        <p className="text-lg text-slate-600 mt-1">{t("library.pageSubtitle")}</p>
      </div>

      {quota && (
        <Card className="p-4" data-testid="library-storage-summary">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-1.5">
            <span>{t("menu.storageUsed")}</span>
            <span className="font-semibold text-slate-700">{fmtBytes(storageUsed)} / {fmtBytes(storageLimit)}</span>
          </div>
          <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${storagePct >= 90 ? "bg-red-500" : storagePct > 80 ? "bg-amber-500" : "bg-blue-600"}`}
              style={{ width: `${storagePct}%` }} />
          </div>
        </Card>
      )}

      <Card className="p-6">
        {/* Breadcrumb: raíz / proyecto / sub-carpeta de cortes */}
        <div className="flex items-center gap-2 text-base mb-4 flex-wrap" data-testid="library-breadcrumb">
          <button onClick={goRoot} className={`flex items-center gap-1 font-bold ${!openProject ? "text-slate-900" : "text-blue-700 hover:text-blue-900"}`}>
            <FolderOpen size={18} className="text-amber-500" /> {t("mf.allVideos")}
          </button>
          {openProject && (
            <>
              <span className="text-slate-400">/</span>
              {openFolder
                ? <button onClick={() => setOpenFolderId(null)} className="font-bold text-blue-700 hover:text-blue-900 truncate max-w-[220px]">{openProject.name}</button>
                : <span className="font-bold text-slate-900 truncate max-w-[260px]">{openProject.name}</span>}
            </>
          )}
          {openFolder && (
            <>
              <span className="text-slate-400">/</span>
              <span className="font-bold text-slate-900 flex items-center gap-1"><Folder size={16} className="text-amber-500" /> {openFolder.name}</span>
            </>
          )}
        </div>

        {folders === null ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={28} /></div>
        ) : !openProject ? (
          /* ---- RAÍZ: una carpeta por vídeo ---- */
          folders.length === 0 ? (
            <Alert type="info">{t("library.empty")}</Alert>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="library-project-folders">
              {folders.map((pf) => (
                <ProjectFolderTile key={pf.project_id} pf={pf} onOpen={() => goProject(pf.project_id)}
                  onDelete={() => deleteWholeFolder(pf)} />
              ))}
            </div>
          )
        ) : openFolder ? (
          /* ---- DENTRO de una sub-carpeta (cortes) ---- */
          <div>
            <button data-testid="library-folder-back" onClick={() => setOpenFolderId(null)}
              className="min-h-[44px] px-3 mb-3 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold flex items-center gap-1 text-slate-700">
              <ArrowLeft size={18} /> {t("mf.back")}
            </button>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {openFolder.parts.map((p) => (
                <ClipCard key={p.id} clip={toClip(p)} displayOnly
                  onDelete={() => deleteItem(openProject.project_id, p.id, p.title)} deleteTestId={`library-delete-item-${p.id}`} />
              ))}
            </div>
          </div>
        ) : (
          /* ---- DENTRO de un vídeo: sus sub-carpetas + archivos sueltos ---- */
          <div>
            <button data-testid="library-project-back" onClick={goRoot}
              className="min-h-[44px] px-3 mb-3 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold flex items-center gap-1 text-slate-700">
              <ArrowLeft size={18} /> {t("mf.back")}
            </button>
            {(openProject.videos.length === 0 && openProject.folders.length === 0) ? (
              <Alert type="info">{t("library.empty")}</Alert>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {openProject.folders.map((f) => (
                  <SubFolderTile key={f.id} folder={f} onOpen={() => setOpenFolderId(f.id)}
                    onDelete={() => deleteItem(openProject.project_id, f.id, f.name)} />
                ))}
                {openProject.videos.map((v) => (
                  <ClipCard key={v.id} clip={toClip(v)} displayOnly
                    onDelete={() => deleteItem(openProject.project_id, v.id, v.title)} deleteTestId={`library-delete-item-${v.id}`} />
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function ProjectFolderTile({ pf, onOpen, onDelete }) {
  const { t } = useTranslation();
  const thumbs = [...(pf.videos || []), ...((pf.folders || []).flatMap((f) => f.parts || []))].slice(0, 4);
  return (
    <div data-testid={`library-project-${pf.project_id}`} className="border-2 border-amber-200 rounded-lg overflow-hidden group bg-amber-50/50 hover:border-amber-400 transition-colors">
      <button onClick={onOpen} className="block w-full text-left" data-testid={`library-open-project-${pf.project_id}`}>
        <div className="relative w-full h-28 bg-amber-100 grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
          {thumbs.map((p, i) => (
            <div key={i} className="bg-slate-300 overflow-hidden">
              {(p.thumbnail_url || p.thumb_path)
                ? <img src={p.thumbnail_url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.thumb_path}`} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-slate-700"><Film size={16} className="text-slate-400" /></div>}
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - thumbs.length) }).map((_, i) => <div key={`e${i}`} className="bg-amber-100" />)}
          <div className="absolute inset-0 bg-amber-900/10 flex items-center justify-center">
            <span className="bg-white/90 rounded-lg px-2 py-1 flex items-center gap-1 text-amber-700 font-bold text-sm shadow"><Folder size={16} /> {t("mf.open")}</span>
          </div>
        </div>
      </button>
      <div className="p-2">
        <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1" title={pf.name}><Folder size={14} className="text-amber-500 shrink-0" /> {pf.name}</div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-xs text-slate-500">{t("mf.filesCount", { count: pf.count })}</span>
          <button data-testid={`library-delete-project-${pf.project_id}`} onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title={t("mf.removeFolderTip")} className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-100 hover:text-red-600">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SubFolderTile({ folder, onOpen, onDelete }) {
  const { t } = useTranslation();
  const count = folder.parts.length;
  const total = folder.parts.reduce((a, p) => a + (p.duration || 0), 0);
  const thumbs = folder.parts.slice(0, 4);
  return (
    <div data-testid={`library-subfolder-${folder.id}`} className="border-2 border-amber-200 rounded-lg overflow-hidden group bg-amber-50/50 hover:border-amber-400 transition-colors">
      <button onClick={onOpen} className="block w-full text-left" data-testid={`library-open-subfolder-${folder.id}`}>
        <div className="relative w-full h-28 bg-amber-100 grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
          {thumbs.map((p, i) => (
            <div key={i} className="bg-slate-300 overflow-hidden">
              {(p.thumbnail_url || p.thumb_path)
                ? <img src={p.thumbnail_url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.thumb_path}`} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-slate-700"><Film size={16} className="text-slate-400" /></div>}
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - thumbs.length) }).map((_, i) => <div key={`e${i}`} className="bg-amber-100" />)}
          <div className="absolute inset-0 bg-amber-900/10 flex items-center justify-center">
            <span className="bg-white/90 rounded-lg px-2 py-1 flex items-center gap-1 text-amber-700 font-bold text-sm shadow"><Folder size={16} /> {t("mf.folder")}</span>
          </div>
        </div>
      </button>
      <div className="p-2">
        <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1" title={folder.name}><Folder size={14} className="text-amber-500 shrink-0" /> {folder.name}</div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-xs text-slate-500">{count} {t("mf.files")} · {fmtTime(total)}</span>
          <button data-testid={`library-delete-subfolder-${folder.id}`} onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-100 hover:text-red-600">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
